import { useState } from 'react';
import styled from '@emotion/styled';

import { Badge } from '@/components/common/Badge';
import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { PortableText } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { colourTokens } from '@/styles/colours';
import { subheadingSmall } from '@/styles/typography';

import type { SanityStackedCardsType } from '@/types/sanity';
import type { BlockWithAudience } from '@/types/shared';

const shortList = 5;

export interface StackedCardsProps
  extends SanityStackedCardsType,
    BlockWithAudience {}
const StackedCards = ({
  heading,
  cards,
  trackingTag,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: StackedCardsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const longList = cards.length > shortList;
  const toggle = () => setIsExpanded(!isExpanded);
  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';
  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="stacked-cards"
    >
      <OuterWrapper>
        <HeadingLevel>
          <StyledCaptionHeadingDescription heading={heading} />
          <CardsContainer
            role="region"
            id="stacked-cards-show-more"
            aria-live="polite"
          >
            <Cards
              cards={cards}
              isExpanded={isExpanded}
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
          </CardsContainer>
          {!!longList && (
            <Button
              variant="flat-light"
              type="button"
              onClick={toggle}
              aria-controls="stacked-cards-show-more"
              aria-expanded={isExpanded}
              className={
                trackingTag?.blockName &&
                `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
              }
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </HeadingLevel>
      </OuterWrapper>
    </Section>
  );
};

interface CardProps extends BlockWithAudience {
  cards: SanityStackedCardsType['cards'];
  isExpanded: boolean;
}

const Cards = ({
  cards,
  isExpanded,
  pageAudience,
  isAudienceSwitcherEnabled,
}: CardProps) => {
  const size = isExpanded ? cards.length : shortList;
  const items = cards.slice(0, size).map((card) => {
    return (
      <CardContainer key={card._key}>
        <Line />
        <Circle />
        <HeadingLevel>
          {card.decorativeColour ? (
            <BadgeParent>
              <Badge bgColour={card.decorativeColour}>{card.heading}</Badge>
            </BadgeParent>
          ) : (
            <Heading size="label-md">{card.heading}</Heading>
          )}
        </HeadingLevel>
        {card.subheading && (
          <Subheading size="heading-md">{card.subheading}</Subheading>
        )}
        <TextWrapper>
          <PortableText
            value={card.body}
            audienceConfig={{ pageAudience, isAudienceSwitcherEnabled }}
          />
        </TextWrapper>
      </CardContainer>
    );
  });
  return items;
};

const OuterWrapper = styled.div`
  ${screen.sm} {
    padding: var(--spacing-800) 0;
  }
  padding: calc(var(--spacing-500) * 2) 0 var(--spacing-800) 0;

  > ${Heading} {
    margin-bottom: var(--spacing-600);
  }
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-600);
`;
const CardContainer = styled.div`
  position: relative;
  background-color: var(${colourTokens.decorative.starlight.token});
  border-radius: var(--radius-l);
  padding: var(--spacing-300);
  margin: var(--spacing-small) 0 var(--spacing-small) var(--spacing-small);
  li {
    margin-left: var(--spacing-200);
    list-style-type: disc;
    list-style-position: outside !important;
  }
  ${screen.sm} {
    padding: calc(var(--spacing-small) * 2);
    margin: calc(var(--spacing-small) * 2) 0;
    margin-left: var(--spacing-500);
    li {
      margin-left: var(--spacing-300);
    }
  }

  ${Heading} {
    padding-bottom: var(--spacing-300);
  }
`;

const Line = styled.div``;

const BadgeParent = styled.div`
  margin-bottom: var(--spacing-small);
`;

const Circle = styled.div`
  background-color: var(--decorative-ultraviolet);
  position: absolute;
  border-radius: 50%;
  top: var(--spacing-200);
  left: calc(var(--spacing-small) * -2);
  transform: translateX(-50%) translateX(2px) translateY(-50%);
  width: 16px;
  height: 16px;
  box-sizing: content-box;
  ${screen.md} {
    width: 20px;
    height: 20px;
  }
`;

const CardsContainer = styled.div`
  padding-left: var(--spacing-small);
  ${CardContainer}:not(:last-child) {
    ${Line} {
      content: '';
      position: absolute;
      width: 2px;
      background-color: #c8c8d5;
      height: calc(100% + var(--spacing-300));
      left: calc(var(--spacing-small) * -2 + 1px);
      top: var(--spacing-200);
    }
  }
  ${screen.md} {
    padding-left: 0;
  }
`;

const Subheading = styled(Paragraph)`
  margin-bottom: 16px;
`;

const TextWrapper = styled.div`
  ${subheadingSmall}
`;

export { StackedCards };
